/// <reference types="vite/client" />
import * as Sentry from '@sentry/react';
import ReactDOM from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter } from 'react-router-dom';
import { I18nextProvider } from 'react-i18next';
import '@teivor/shared-ui/styles';
import { i18n } from '@teivor/shared-utils/i18n';
import { configureApi } from '@teivor/shared-utils/api';

import { applyAtBoot } from './lib/theme';
import { App } from './App';

void i18n;

// Apply the persisted theme/density/locale before first render so the portal
// honours the user's choices on the first paint (no flash) — mirrors the admin
// app. Without this the portal had no theme switching at all and language
// changes were not persisted across reloads.
applyAtBoot();

// Wire the shared v4 API client's 401 handling to the client-portal sign-in
// route. Without this the portal renders its authenticated shell for an
// unauthenticated visitor (the API 401s but nothing redirects) — i.e. you land
// "in the system" without logging in. Mirrors apps/admin/src/main.tsx. The
// portal is client-scoped: the backend resolves the workspace from the signed-in
// client (com BR-13), so no x-workspace-id header is sent.
configureApi({
  onUnauthorized: () => {
    const path = window.location.pathname;
    if (path.startsWith('/login') || path.startsWith('/auth/')) return;
    const ret = encodeURIComponent(window.location.pathname + window.location.search);
    window.location.assign(`/login?return=${ret}`);
  },
});

// Initialise Sentry before React renders so breadcrumbs and replay start immediately.
if (import.meta.env.VITE_SENTRY_DSN) {
  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN as string,
    integrations: [Sentry.browserTracingIntegration()],
    tracesSampleRate: 0.1,
  });
  // Expose globally so any error boundary can call captureException without a
  // hard import dependency (tree-shaking friendly; also works if Sentry fails to load).
  (window as unknown as Record<string, unknown>).Sentry = Sentry;
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { staleTime: 30_000, retry: 1, refetchOnWindowFocus: false },
  },
});

ReactDOM.createRoot(document.getElementById('root')!).render(
  <QueryClientProvider client={queryClient}>
    <I18nextProvider i18n={i18n}>
      <BrowserRouter>
        <App />
      </BrowserRouter>
    </I18nextProvider>
  </QueryClientProvider>,
);
